import eventMixin from '../lib/mixins/event';
import Logger from '../lib/logger';
import Player from '@vimeo/player';
import { v4 as uuidv4 } from 'uuid';

export default class ThemeBannerHero {

	constructor(dom) {

		// Vars
		// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
		this.NAME = `ThemeBannerHero`;

		this.dom = dom;

		this.videoDom = dom.querySelector('[data-theme-banner-hero-video]');

		const uuid = uuidv4();
		this.videoDom?.setAttribute('id', uuid);

		this.autoplay = this.videoDom?.getAttribute(`data-theme-banner-hero-video-autoplay`);

		this.buttonArrowDom = this.dom.querySelector(`[data-theme-banner-hero-button-arrow]`);

		this.placeholderImageDoms = dom.querySelectorAll(`[data-theme-banner-hero-placeholder-image]`);
		this.placeholderImageDomHiddenClass = `theme-banner-hero__image--hidden`;
		this.vimeoPosterDom = dom.querySelector(`[data-theme-banner-hero-vimeo-poster]`);

		this.playButtonDom = dom.querySelector('theme-play-button');

		this.vimeoIdDesktop = this.videoDom?.getAttribute('data-theme-banner-hero-video');
		this.vimeoIdMobile = this.videoDom?.getAttribute('data-theme-banner-hero-video-mobile');
		this.videoPlayer = null;

		this.thresholdDom = this.dom.querySelector(`[data-theme-banner-hero-threshold]`);
		this.thresholdDomId = `theme-banner-hero-video-${uuid}`;
		this.thresholdDom.setAttribute(`id`, this.thresholdDomId);
	}

	// Logic
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
	init() {
		Logger.info(`${this.NAME} > init`);

		const self = this;

		this.loadVimeoPoster();

		/**
		* Initialize the Vimeo Player when visible
		*/
		if (this.videoDom) {
			const observer = new IntersectionObserver(entries => {
				entries.forEach(entry => {
					const intersecting = entry.isIntersecting;

					if (intersecting && !this.videoPlayer) {
						self.initializeVideoPlayer();
					}
				})
			})

			observer.observe(this.videoDom);
		}

		this.buttonArrowDom?.addEventListener(`click`, (e) => {
			e.preventDefault();
			self.thresholdDom.scrollIntoView(true);
		});

	}

	async loadVimeoPoster() {
		if (!this.vimeoPosterDom) return;

		const vimeoId = this.getVimeoIdForScreenSize();
		const imageDom = this.vimeoPosterDom.querySelector('img');

		try {
			const response = await fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${encodeURIComponent(vimeoId)}&width=1920`);
			if (!response.ok) throw new Error(`Vimeo oEmbed request failed: ${response.status}`);

			const video = await response.json();
			if (video.thumbnail_url && imageDom) {
				imageDom.src = video.thumbnail_url;
				imageDom.alt = video.title || '';
			}
		} catch (error) {
			Logger.error(`${this.NAME} > loadVimeoPoster`, error);
		}
	}

	getVimeoIdForScreenSize() {
		Logger.info(`${this.NAME} > hidePlayButton`);

		// If desktop
		if (window.innerWidth > 1024) {
			return this.vimeoIdDesktop;
		}

		return this.vimeoIdMobile ? this.vimeoIdMobile : this.vimeoIdDesktop;
	}

	hidePlayButton() {
		Logger.info(`${this.NAME} > hidePlayButton`);
		if (this.playButtonDom) {
			this.playButtonDom.dataset.playing = 'true';
		}
	}

	initializeVideoPlayer() {
		Logger.info(`${this.NAME} > initializeVideoPlayer`);

		const self = this;

		const vimeoId = this.getVimeoIdForScreenSize();

		this.videoPlayer = new Player(this.videoDom.getAttribute('id'), {
			id: vimeoId,
			controls: false,
			title: false,
			responsive: true,
		});

		// Listen for ended
		this.videoPlayer.on('ended', function () {
			self.showPlayButton();
			self.videoPlayer.setCurrentTime(0);
		});

		// Listen for play
		this.videoPlayer.on('play', function () {
			self.hidePlayButton();
			self.hidePlaceholderImages();
		});

		// Listen for pause
		this.videoPlayer.on('pause', function () {
			self.showPlayButton();
		});

		if (this.playButtonDom) {
			this.playButtonDom.addEventListener('click', (e) => {
				e.preventDefault();
				const isPaused = this.videoPlayer.getPaused().then((isPaused) => {
					if (isPaused) {
						self.playVideo();
					} else {
						self.pauseVideo()
					}
				});
			});
		}

		// Check for autoplay
		const autoplayBool = (this.autoplay === 'true');
		if (autoplayBool) {
			Logger.info(`${this.NAME} > initializeVideoPlayer > autoplay`);
			this.playVideo();
		} else {
			this.showPlayButton();
		}

		// Listen for resize
		window.onresize = this.loadVideoForScreenSize.bind(this);
	}

	hidePlaceholderImages() {
		Logger.info(`${this.NAME} > hidePlaceholderImages`);
		this.placeholderImageDoms.forEach((v) => {
			v.classList.add(this.placeholderImageDomHiddenClass);
		});
	}

	async loadVideoForScreenSize() {
		Logger.info(`${this.NAME} > loadVideoForScreenSize`);

		const currentVimeoId = await this.videoPlayer?.getVideoId();

		const vimeoId = this.getVimeoIdForScreenSize();

		if (parseInt(currentVimeoId) !== parseInt(vimeoId)) {
			Logger.info(`${this.NAME} > loadVideoForScreenSize > switching video`);
			this.loadVideo(vimeoId);
		}
	}

	async loadVideo(vimeoId) {
		Logger.info(`${this.NAME} > loadVideo`);
		await this.videoPlayer?.loadVideo(vimeoId);
		this.playVideo();
	}

	muteVideo() {
		Logger.info(`${this.NAME} > muteVideo`);
		this.videoPlayer?.setVolume(0);
	}

	playVideo() {
		Logger.info(`${this.NAME} > playVideo`);
		this.hidePlayButton();
		this.muteVideo();
		this.videoPlayer?.play();
	}

	pauseVideo() {
		Logger.info(`${this.NAME} > pauseVideo`);
		this.videoPlayer?.pause();
	}

	showPlaceholderImages() {
		Logger.info(`${this.NAME} > showPlaceholderImages`);
		this.placeholderImageDoms.forEach((v) => {
			v.classList.remove(this.placeholderImageDomHiddenClass);
		});
	}

	showPlayButton() {
		Logger.info(`${this.NAME} > showPlayButton`);
		if (this.playButtonDom) {
			this.playButtonDom.dataset.playing = 'false';
		}
	}
}

// Mixins
Object.assign(ThemeBannerHero.prototype, eventMixin);
